/**
 * GLOWSERA QUIZ - MOTEUR PRINCIPAL
 * Gestion du flow des questions, navigation, collecte reponses et tracking
 * Compatible Shopify Cornerstone 4.2.2
 */

class GlowseraQuiz {
  /**
   * Constructeur du quiz
   * @param {string} quizType - Type de quiz (skin, hair, makeup, body)
   * @param {Object} config - Configuration du quiz depuis glowsera-quiz-data.js
   */
  constructor(quizType, config) {
    console.log('[Glowsera Quiz] Initialisation du quiz:', quizType);

    this.quizType = quizType;
    this.config = config;
    this.currentQuestionIndex = 0;
    this.userAnswers = {};
    this.questionHistory = []; // Pour la navigation retour
    this.startTime = Date.now();
    this.urlSyncEnabled = true; // Activer la synchronisation URL

    // Elements DOM
    this.container = document.getElementById('glowsera-quiz-container');
    this.progressBar = document.getElementById('quiz-progress-bar');
    this.progressText = document.getElementById('quiz-progress-text');
    this.questionContainer = document.getElementById('quiz-question-container');
    this.prevButton = document.getElementById('quiz-prev-button');
    this.nextButton = document.getElementById('quiz-next-button');

    // Bind methods
    this.handleNext = this.handleNext.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.handleAnswerSelect = this.handleAnswerSelect.bind(this);

    // Init
    this.init();
  }

  /**
   * Initialisation du quiz
   */
  init() {
    try {
      // Verifier que les donnees du quiz existent
      if (!this.config || !this.config.questions) {
        throw new Error('Configuration du quiz invalide');
      }

      // Attacher les event listeners
      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrev);
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNext);
      }

      // Charger les reponses depuis l'URL si disponibles
      const shouldShowResults = this.loadAnswersFromURL();

      // Tracker le demarrage du quiz
      this.trackEvent('quiz_started', {
        quiz_type: this.quizType
      });

      // Si on doit afficher les resultats directement, sauter les questions
      if (shouldShowResults) {
        console.log('[Glowsera Quiz] Affichage direct des resultats depuis l\'URL');
        this.completeQuiz();
      } else {
        // Afficher la premiere question
        this.renderQuestion();
      }

      console.log('[Glowsera Quiz] Initialisation terminee');
    } catch (error) {
      console.error('[Glowsera Quiz] Erreur initialisation:', error);
      this.showError('Une erreur est survenue lors du chargement du quiz.');
    }
  }

  /**
   * Obtenir la question courante
   * @returns {Object} Question courante
   */
  getCurrentQuestion() {
    return this.config.questions[this.currentQuestionIndex];
  }

  /**
   * Calculer le nombre total de questions (sans les questions skip)
   * @returns {number} Nombre total
   */
  getTotalQuestions() {
    return this.config.questions.filter(q => !this.shouldSkipQuestion(q)).length;
  }

  /**
   * Verifier si une question doit etre skip selon la logique conditionnelle
   * @param {Object} question - Question a verifier
   * @returns {boolean} True si skip
   */
  shouldSkipQuestion(question) {
    if (!question.skipIf) return false;

    // Verifier chaque condition de skip
    for (const condition of question.skipIf) {
      const answer = this.userAnswers[condition.questionId];

      if (condition.operator === 'equals' && answer === condition.value) {
        return true;
      }
      if (condition.operator === 'notEquals' && answer !== condition.value) {
        return true;
      }
      if (condition.operator === 'includes' && Array.isArray(answer) && answer.includes(condition.value)) {
        return true;
      }
      if (condition.operator === 'notIncludes' && Array.isArray(answer) && !answer.includes(condition.value)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Trouver le prochain index de question valide
   * @param {number} currentIndex - Index actuel
   * @param {number} direction - Direction (1 = suivant, -1 = precedent)
   * @returns {number} Prochain index valide ou -1 si fin
   */
  findNextValidQuestionIndex(currentIndex, direction = 1) {
    let nextIndex = currentIndex + direction;

    while (nextIndex >= 0 && nextIndex < this.config.questions.length) {
      const question = this.config.questions[nextIndex];

      if (!this.shouldSkipQuestion(question)) {
        return nextIndex;
      }

      nextIndex += direction;
    }

    return -1; // Pas de question valide trouvee
  }

  /**
   * Rendre la question courante dans le DOM
   */
  renderQuestion() {
    const question = this.getCurrentQuestion();

    if (!question) {
      console.error('[Glowsera Quiz] Question non trouvee');
      return;
    }

    // Verifier si cette question doit etre skip
    if (this.shouldSkipQuestion(question)) {
      console.log('[Glowsera Quiz] Question skipee:', question.id);
      this.handleNext(true); // Auto-skip
      return;
    }

    console.log('[Glowsera Quiz] Rendu question:', question.id);

    // Update progress bar
    this.updateProgress();

    // Creer le HTML de la question
    let questionHTML = '';

    if (question.type === 'email') {
      questionHTML = this.renderEmailQuestion(question);
    } else if (question.type === 'text') {
      questionHTML = this.renderTextQuestion(question);
    } else {
      questionHTML = this.renderStandardQuestion(question);
    }

    // Animer la transition
    this.animateQuestionTransition(questionHTML);

    // Update boutons navigation
    this.updateNavigationButtons();

    // Tracker la question vue
    this.trackEvent('quiz_question_answered', {
      quiz_type: this.quizType,
      question_id: question.id,
      question_index: this.currentQuestionIndex
    });
  }

  /**
   * Rendre une question standard (single/multiple choice)
   * @param {Object} question - Question a rendre
   * @returns {string} HTML
   */
  renderStandardQuestion(question) {
    const savedAnswer = this.userAnswers[question.id];

    let html = `
      <div class="quiz-question" data-question-id="${question.id}">
        <h2 class="quiz-question-title">${question.question}</h2>
        ${question.subtitle ? `<p class="quiz-question-subtitle">${question.subtitle}</p>` : ''}

        <div class="quiz-answers ${question.type === 'multiple' ? 'quiz-answers-multiple' : ''}">
    `;

    question.answers.forEach((answer) => {
      const isSelected = question.type === 'multiple'
        ? (Array.isArray(savedAnswer) && savedAnswer.includes(answer.value))
        : savedAnswer === answer.value;

      html += `
        <button
          class="quiz-answer-button ${isSelected ? 'selected' : ''}"
          data-value="${answer.value}"
          data-question-type="${question.type}"
        >
          <span class="quiz-answer-text">${answer.label}</span>
          ${answer.subtitle ? `<span class="quiz-answer-subtitle">${answer.subtitle}</span>` : ''}
        </button>
      `;
    });

    html += `
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Rendre la question email avec consentement RGPD
   * @param {Object} question - Question email
   * @returns {string} HTML
   */
  renderEmailQuestion(question) {
    const savedEmail = this.userAnswers[question.id] || '';
    const savedConsent = this.userAnswers['consent'] || false;

    let html = `
      <div class="quiz-question quiz-question-email" data-question-id="${question.id}">
        <h2 class="quiz-question-title">${question.question}</h2>
        ${question.subtitle ? `<p class="quiz-question-subtitle">${question.subtitle}</p>` : ''}

        <div class="quiz-email-form">
          <div class="quiz-form-group">
            <label for="quiz-email-input" class="quiz-form-label">Ton email</label>
            <input
              type="email"
              id="quiz-email-input"
              class="quiz-email-input"
              placeholder="exemple@email.fr"
              value="${savedEmail}"
              ${question.required ? 'required' : ''}
            />
            <span class="quiz-email-error" style="display: none;">Entre un email valide</span>
          </div>

          ${question.collectName ? `
            <div class="quiz-form-group">
              <label for="quiz-name-input" class="quiz-form-label">Ton prénom (optionnel)</label>
              <input
                type="text"
                id="quiz-name-input"
                class="quiz-name-input"
                placeholder="Prenom"
                value="${this.userAnswers['name'] || ''}"
              />
            </div>
          ` : ''}

          <div class="quiz-consent-group">
            <label class="quiz-consent-label">
              <input
                type="checkbox"
                id="quiz-consent-checkbox"
                class="quiz-consent-checkbox"
                ${savedConsent ? 'checked' : ''}
                ${question.requireConsent ? 'required' : ''}
              />
              <span class="quiz-consent-text">
                J'accepte de recevoir des recommandations personnalisees et des offres exclusives de Glowsera par email.
                Tu peux te désinscrire à tout moment.
              </span>
            </label>
          </div>

          <p class="quiz-email-skip">
            Tu peux aussi <button type="button" class="quiz-skip-button">passer cette étape</button>
          </p>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Rendre la question text input (prenom, etc.)
   * @param {Object} question - Question text
   * @returns {string} HTML
   */
  renderTextQuestion(question) {
    const savedValue = this.userAnswers[question.id] || '';

    let html = `
      <div class="quiz-question quiz-question-text" data-question-id="${question.id}">
        <h2 class="quiz-question-title">${question.question}</h2>
        ${question.subtitle ? `<p class="quiz-question-subtitle">${question.subtitle}</p>` : ''}

        <div class="quiz-text-form">
          <div class="quiz-form-group">
            <input
              type="text"
              id="quiz-text-input"
              class="quiz-text-input"
              placeholder="${question.placeholder || ''}"
              value="${savedValue}"
              ${question.required ? 'required' : ''}
            />
            <span class="quiz-text-error" style="display: none;">Ce champ est requis</span>
          </div>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Animer la transition entre questions
   * @param {string} newHTML - Nouveau HTML a afficher
   */
  animateQuestionTransition(newHTML) {
    if (!this.questionContainer) return;

    // Fade out
    this.questionContainer.style.opacity = '0';
    this.questionContainer.style.transform = 'translateY(20px)';

    setTimeout(() => {
      // Remplacer le contenu
      this.questionContainer.innerHTML = newHTML;

      // Attacher les event listeners aux nouvelles reponses
      this.attachAnswerListeners();

      // Fade in
      setTimeout(() => {
        this.questionContainer.style.opacity = '1';
        this.questionContainer.style.transform = 'translateY(0)';
      }, 50);
    }, 300);
  }

  /**
   * Attacher les event listeners aux boutons de reponse
   */
  attachAnswerListeners() {
    const answerButtons = this.questionContainer.querySelectorAll('.quiz-answer-button');
    const emailInput = this.questionContainer.querySelector('#quiz-email-input');
    const textInput = this.questionContainer.querySelector('#quiz-text-input');
    const nameInput = this.questionContainer.querySelector('#quiz-name-input');
    const consentCheckbox = this.questionContainer.querySelector('#quiz-consent-checkbox');
    const skipButton = this.questionContainer.querySelector('.quiz-skip-button');

    // Boutons reponses standard
    answerButtons.forEach(button => {
      button.addEventListener('click', this.handleAnswerSelect);
    });

    // Champ email
    if (emailInput) {
      emailInput.addEventListener('input', () => {
        const errorSpan = this.questionContainer.querySelector('.quiz-email-error');
        if (errorSpan) {
          errorSpan.style.display = 'none';
        }
      });
    }

    // Champ text (prenom, etc.)
    if (textInput) {
      const question = this.getCurrentQuestion();
      textInput.addEventListener('input', (e) => {
        const value = e.target.value.trim();
        this.userAnswers[question.id] = value;
        this.updateNavigationButtons();

        const errorSpan = this.questionContainer.querySelector('.quiz-text-error');
        if (errorSpan) {
          errorSpan.style.display = 'none';
        }
      });

      // Auto-focus sur le champ
      setTimeout(() => textInput.focus(), 350);
    }

    // Champ prenom
    if (nameInput) {
      nameInput.addEventListener('input', (e) => {
        this.userAnswers['name'] = e.target.value.trim();
      });
    }

    // Checkbox consentement
    if (consentCheckbox) {
      consentCheckbox.addEventListener('change', (e) => {
        this.userAnswers['consent'] = e.target.checked;
      });
    }

    // Bouton skip email
    if (skipButton) {
      skipButton.addEventListener('click', () => {
        this.handleNext();
      });
    }
  }

  /**
   * Gerer la selection d'une reponse
   * @param {Event} event - Event click
   */
  handleAnswerSelect(event) {
    const button = event.currentTarget;
    const questionId = button.closest('.quiz-question').dataset.questionId;
    const value = button.dataset.value;
    const questionType = button.dataset.questionType;

    console.log('[Glowsera Quiz] Reponse selectionnee:', { questionId, value, questionType });

    if (questionType === 'multiple') {
      // Multi-selection
      const currentAnswers = this.userAnswers[questionId] || [];

      if (currentAnswers.includes(value)) {
        // Deselectionner
        this.userAnswers[questionId] = currentAnswers.filter(v => v !== value);
        button.classList.remove('selected');
      } else {
        // Selectionner
        this.userAnswers[questionId] = [...currentAnswers, value];
        button.classList.add('selected');
      }
    } else {
      // Selection simple
      this.userAnswers[questionId] = value;

      // Update visual
      const allButtons = button.closest('.quiz-answers').querySelectorAll('.quiz-answer-button');
      allButtons.forEach(btn => btn.classList.remove('selected'));
      button.classList.add('selected');

      // Auto-avancer apres 300ms
      setTimeout(() => {
        this.handleNext();
      }, 300);
    }

    // Update bouton suivant
    this.updateNavigationButtons();

    // Mettre a jour l'URL avec les reponses
    this.updateURLWithAnswers();
  }

  /**
   * Valider que la question courante a une reponse
   * @returns {boolean} True si valide
   */
  validateCurrentQuestion() {
    const question = this.getCurrentQuestion();
    const answer = this.userAnswers[question.id];

    // Question text (prenom, etc.)
    if (question.type === 'text') {
      const textInput = this.questionContainer.querySelector('#quiz-text-input');
      const errorSpan = this.questionContainer.querySelector('.quiz-text-error');

      if (!textInput) return true; // Skip si pas d'input

      const textValue = textInput.value.trim();

      // Si vide et requis, invalide
      if (!textValue && question.required) {
        if (errorSpan) {
          errorSpan.style.display = 'block';
        }
        return false;
      }

      // Sauvegarder la valeur
      if (textValue) {
        this.userAnswers[question.id] = textValue;

        // Si c'est le prenom, aussi sauvegarder dans 'name' pour compatibilite
        if (question.id === 'user-name') {
          this.userAnswers['name'] = textValue;
        }
      }

      return true;
    }

    // Question email
    if (question.type === 'email') {
      const emailInput = this.questionContainer.querySelector('#quiz-email-input');
      const consentCheckbox = this.questionContainer.querySelector('#quiz-consent-checkbox');
      const errorSpan = this.questionContainer.querySelector('.quiz-email-error');

      if (!emailInput) return true; // Skip si pas d'input

      const emailValue = emailInput.value.trim();

      // Si email vide et non requis, OK
      if (!emailValue && !question.required) {
        return true;
      }

      // Valider format email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailValue)) {
        if (errorSpan) {
          errorSpan.style.display = 'block';
        }
        return false;
      }

      // Valider consentement si requis
      if (question.requireConsent && consentCheckbox && !consentCheckbox.checked) {
        alert('Accepte de recevoir nos recommandations pour continuer.');
        return false;
      }

      // Sauvegarder l'email
      this.userAnswers[question.id] = emailValue;

      // Sauvegarder le prenom si renseigne
      const nameInput = this.questionContainer.querySelector('#quiz-name-input');
      if (nameInput && nameInput.value.trim()) {
        this.userAnswers['name'] = nameInput.value.trim();
      }

      return true;
    }

    // Questions standard
    if (question.required && !answer) {
      return false;
    }

    if (question.type === 'multiple' && question.required && (!Array.isArray(answer) || answer.length === 0)) {
      return false;
    }

    return true;
  }

  /**
   * Gerer le clic sur bouton Suivant
   * @param {boolean} autoSkip - True si auto-skip de question
   */
  handleNext(autoSkip = false) {
    console.log('[Glowsera Quiz] Navigation suivant');

    // Valider la reponse (sauf si auto-skip)
    if (!autoSkip && !this.validateCurrentQuestion()) {
      console.log('[Glowsera Quiz] Question invalide');
      return;
    }

    // Sauvegarder dans l'historique (pour retour arriere)
    if (!autoSkip) {
      this.questionHistory.push(this.currentQuestionIndex);
    }

    // Trouver la prochaine question valide
    const nextIndex = this.findNextValidQuestionIndex(this.currentQuestionIndex, 1);

    if (nextIndex === -1) {
      // Fin du quiz
      console.log('[Glowsera Quiz] Fin du quiz atteinte');
      this.completeQuiz();
    } else {
      // Passer a la question suivante
      this.currentQuestionIndex = nextIndex;
      this.renderQuestion();
    }
  }

  /**
   * Gerer le clic sur bouton Precedent
   */
  handlePrev() {
    console.log('[Glowsera Quiz] Navigation precedent');

    if (this.questionHistory.length === 0) {
      console.log('[Glowsera Quiz] Debut du quiz, impossible de revenir en arriere');
      return;
    }

    // Recuperer le dernier index de l'historique
    this.currentQuestionIndex = this.questionHistory.pop();
    this.renderQuestion();
  }

  /**
   * Mettre a jour la barre de progression
   */
  updateProgress() {
    if (!this.progressBar || !this.progressText) return;

    const totalQuestions = this.getTotalQuestions();

    // Compter combien de questions valides ont ete vues jusqu'a maintenant
    let validQuestionsCount = 0;
    for (let i = 0; i <= this.currentQuestionIndex; i++) {
      const question = this.config.questions[i];
      if (question && !this.shouldSkipQuestion(question)) {
        validQuestionsCount++;
      }
    }

    const currentPosition = validQuestionsCount;
    const percentage = (currentPosition / totalQuestions) * 100;

    this.progressBar.style.width = `${percentage}%`;
    this.progressText.textContent = `Question ${currentPosition} sur ${totalQuestions}`;

    console.log('[Glowsera Quiz] Progression:', { currentPosition, totalQuestions, percentage });
  }

  /**
   * Mettre a jour l'etat des boutons de navigation
   */
  updateNavigationButtons() {
    if (!this.prevButton || !this.nextButton) return;

    const question = this.getCurrentQuestion();

    // Bouton precedent
    this.prevButton.disabled = this.questionHistory.length === 0;
    this.prevButton.style.opacity = this.questionHistory.length === 0 ? '0.5' : '1';

    // Bouton suivant
    const hasAnswer = this.validateCurrentQuestion();

    if (question.type === 'multiple') {
      // Pour multi-select, toujours actif
      this.nextButton.disabled = false;
      this.nextButton.style.opacity = '1';
    } else if (question.type === 'email') {
      // Pour email, actif si email valide ou skip
      this.nextButton.disabled = false;
      this.nextButton.style.opacity = '1';
      this.nextButton.textContent = 'Voir mes recommandations';
    } else if (question.type === 'text') {
      // Pour text input, actif si texte saisi
      this.nextButton.disabled = !hasAnswer;
      this.nextButton.style.opacity = hasAnswer ? '1' : '0.5';
    } else {
      // Pour single choice, desactive si pas de reponse
      this.nextButton.disabled = !hasAnswer;
      this.nextButton.style.opacity = hasAnswer ? '1' : '0.5';
    }
  }

  /**
   * Completer le quiz et afficher les resultats
   */
  async completeQuiz() {
    console.log('[Glowsera Quiz] Completion du quiz');
    console.log('[Glowsera Quiz] Reponses utilisateur:', this.userAnswers);

    // Mettre a jour l'URL avec le flag results=true
    this.updateURLForResults();

    // Calculer le temps passe
    const completionTime = Math.round((Date.now() - this.startTime) / 1000);

    // Tracker la completion
    this.trackEvent('quiz_completed', {
      quiz_type: this.quizType,
      completion_time: completionTime,
      email_collected: !!this.userAnswers[this.config.questions.find(q => q.type === 'email')?.id],
      total_questions: this.getTotalQuestions()
    });

    // Envoyer l'email a Klaviyo si collecte
    const emailQuestion = this.config.questions.find(q => q.type === 'email');
    if (emailQuestion && this.userAnswers[emailQuestion.id]) {
      await this.sendToKlaviyo(this.userAnswers[emailQuestion.id]);
    }

    // Generer le profil utilisateur pour les recommandations
    const userProfile = this.generateUserProfile();
    console.log('[Glowsera Quiz] Profil utilisateur genere:', userProfile);

    // Afficher l'ecran de chargement
    this.showLoadingResults();

    // Charger les recommandations via l'API
    try {
      if (typeof window.GlowseraRecommendationEngine === 'undefined') {
        throw new Error('GlowseraRecommendationEngine non charge');
      }

      const engine = new window.GlowseraRecommendationEngine(this.quizType, this.config);
      const recommendations = await engine.getRecommendations(userProfile);

      console.log('[Glowsera Quiz] Recommandations recues:', recommendations);

      // Afficher les resultats
      this.showResults(recommendations, userProfile);

    } catch (error) {
      console.error('[Glowsera Quiz] Erreur chargement recommandations:', error);
      this.showError('Impossible de charger les recommandations. Essaye à nouveau.');
    }
  }

  /**
   * Generer le profil utilisateur a partir des reponses
   * @returns {Object} Profil utilisateur
   */
  generateUserProfile() {
    const profile = {
      quizType: this.quizType,
      answers: this.userAnswers,
      tags: [],
      concerns: [],
      categories: [],
      preferences: {}
    };

    // Parcourir les questions et extraire les tags/preferences
    this.config.questions.forEach((question) => {
      const answer = this.userAnswers[question.id];

      if (!answer || question.type === 'email') return;

      // Trouver les objets answer correspondants
      const answerObjects = question.answers.filter(a => {
        if (Array.isArray(answer)) {
          return answer.includes(a.value);
        }
        return a.value === answer;
      });

      // Extraire les tags
      answerObjects.forEach((answerObj) => {
        if (answerObj.tags) {
          profile.tags.push(...answerObj.tags);
        }

        // Extraire les concerns
        if (answerObj.tags) {
          const concernTags = answerObj.tags.filter(tag => tag.startsWith('concern-'));
          profile.concerns.push(...concernTags);
        }

        // Stocker les preferences specifiques
        if (question.id === 'finish' || question.id === 'hair-goals') {
          profile.preferences[question.id] = answer;
        }
      });
    });

    // Dedupliquer les tags
    profile.tags = [...new Set(profile.tags)];
    profile.concerns = [...new Set(profile.concerns)];

    // Ajouter le nom et email si disponibles
    if (this.userAnswers['name']) {
      profile.name = this.userAnswers['name'];
    }

    const emailQuestion = this.config.questions.find(q => q.type === 'email');
    if (emailQuestion && this.userAnswers[emailQuestion.id]) {
      profile.email = this.userAnswers[emailQuestion.id];
    }

    return profile;
  }

  /**
   * Afficher l'ecran de chargement des resultats
   */
  showLoadingResults() {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="quiz-loading">
        <div class="quiz-loading-spinner"></div>
        <h2>Analyse de ton profil en cours...</h2>
        <p>Nous sélectionnons les meilleurs produits pour toi</p>
      </div>
    `;
  }

  /**
   * Afficher les resultats avec recommandations
   * @param {Array} recommendations - Produits recommandes
   * @param {Object} userProfile - Profil utilisateur
   */
  showResults(recommendations, userProfile) {
    if (!this.container) return;

    console.log('[Glowsera Quiz] Affichage des resultats');

    // Generer l'URL partageable
    const shareableURL = this.generateShareableURL(userProfile);

    // Utiliser le snippet Liquid pour l'affichage
    // On va stocker les donnees dans un attribut data pour que Liquid puisse les lire
    window.glowseraQuizResults = {
      recommendations: recommendations,
      userProfile: userProfile,
      quizType: this.quizType,
      shareableURL: shareableURL
    };

    // Declencher un event pour que le snippet Liquid se charge
    const event = new CustomEvent('glowsera:quiz:completed', {
      detail: {
        recommendations: recommendations,
        userProfile: userProfile,
        quizType: this.quizType,
        shareableURL: shareableURL
      }
    });
    document.dispatchEvent(event);

    // Tracker les produits recommandes
    recommendations.forEach((product, index) => {
      this.trackEvent('quiz_product_recommended', {
        product_id: product.id,
        product_name: product.title,
        score: product.score,
        position: index + 1,
        quiz_type: this.quizType
      });
    });
  }

  /**
   * Afficher un message d'erreur
   * @param {string} message - Message d'erreur
   */
  showError(message) {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="quiz-error">
        <h2>Oups, une erreur est survenue</h2>
        <p>${message}</p>
        <button class="quiz-retry-button" onclick="location.reload()">Reessayer</button>
      </div>
    `;
  }

  /**
   * Envoyer l'email a Klaviyo
   * @param {string} email - Email utilisateur
   */
  async sendToKlaviyo(email) {
    try {
      console.log('[Glowsera Quiz] Envoi email a Klaviyo:', email);

      // Recuperer la cle publique Klaviyo depuis les meta tags
      const klaviyoKeyMeta = document.querySelector('meta[name="klaviyo-public-key"]');
      const klaviyoKey = klaviyoKeyMeta ? klaviyoKeyMeta.content : null;

      if (!klaviyoKey) {
        console.warn('[Glowsera Quiz] Cle Klaviyo non trouvee, skip envoi');
        return;
      }

      // Preparer les proprietes
      const properties = {
        email: email,
        quiz_type: this.quizType,
        quiz_completed: true,
        quiz_date: new Date().toISOString()
      };

      // Ajouter le prenom si disponible
      if (this.userAnswers['name']) {
        properties.$first_name = this.userAnswers['name'];
      }

      // Ajouter les reponses importantes
      Object.keys(this.userAnswers).forEach((key) => {
        if (key !== 'email' && key !== 'consent' && key !== 'name') {
          properties[`quiz_${key}`] = this.userAnswers[key];
        }
      });

      // Envoyer a Klaviyo
      const response = await fetch('https://a.klaviyo.com/api/identify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: klaviyoKey,
          properties: properties
        })
      });

      if (!response.ok) {
        throw new Error('Erreur envoi Klaviyo');
      }

      console.log('[Glowsera Quiz] Email envoye a Klaviyo avec succes');

    } catch (error) {
      console.error('[Glowsera Quiz] Erreur envoi Klaviyo:', error);
      // Ne pas bloquer le quiz si erreur Klaviyo
    }
  }

  /**
   * Charger les reponses depuis les parametres URL
   * @returns {boolean} True si on doit afficher les resultats directement
   */
  loadAnswersFromURL() {
    if (!this.urlSyncEnabled) return false;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const answersParam = urlParams.get('answers');

      if (!answersParam) {
        console.log('[Glowsera Quiz] Aucune reponse dans l\'URL');
        return false;
      }

      // Decoder les reponses depuis l'URL
      const decodedAnswers = JSON.parse(decodeURIComponent(answersParam));
      this.userAnswers = decodedAnswers;

      console.log('[Glowsera Quiz] Reponses chargees depuis l\'URL:', this.userAnswers);

      // Verifier si on doit afficher les resultats directement
      const showResults = urlParams.get('results') === 'true';

      if (showResults) {
        console.log('[Glowsera Quiz] Parametre results=true detecte');
        return true;
      }

      // Restaurer la progression
      const progressParam = urlParams.get('progress');
      if (progressParam) {
        const progress = parseInt(progressParam, 10);
        if (!isNaN(progress) && progress >= 0 && progress < this.config.questions.length) {
          this.currentQuestionIndex = progress;

          // Reconstruire l'historique
          for (let i = 0; i < progress; i++) {
            this.questionHistory.push(i);
          }
        }
      }

      console.log('[Glowsera Quiz] Progression restauree:', this.currentQuestionIndex);

      return false;

    } catch (error) {
      console.error('[Glowsera Quiz] Erreur chargement reponses depuis URL:', error);
      // En cas d'erreur, on continue avec un quiz vide
      return false;
    }
  }

  /**
   * Mettre a jour l'URL avec les reponses actuelles
   */
  updateURLWithAnswers() {
    if (!this.urlSyncEnabled) return;

    try {
      const urlParams = new URLSearchParams(window.location.search);

      // Encoder les reponses en JSON
      const answersJSON = JSON.stringify(this.userAnswers);
      urlParams.set('answers', encodeURIComponent(answersJSON));

      // Ajouter la progression actuelle
      urlParams.set('progress', this.currentQuestionIndex.toString());

      // Mettre a jour l'URL sans recharger la page
      const newURL = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({ answers: this.userAnswers, progress: this.currentQuestionIndex }, '', newURL);

      console.log('[Glowsera Quiz] URL mise a jour avec les reponses');

    } catch (error) {
      console.error('[Glowsera Quiz] Erreur mise a jour URL:', error);
    }
  }

  /**
   * Mettre a jour l'URL pour indiquer qu'on est sur les resultats
   */
  updateURLForResults() {
    if (!this.urlSyncEnabled) return;

    try {
      const urlParams = new URLSearchParams(window.location.search);

      // Encoder les reponses en JSON
      const answersJSON = JSON.stringify(this.userAnswers);
      urlParams.set('answers', encodeURIComponent(answersJSON));

      // Marquer comme page de resultats
      urlParams.set('results', 'true');

      // Supprimer le parametre progress (plus necessaire)
      urlParams.delete('progress');

      // Mettre a jour l'URL sans recharger la page
      const newURL = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({ answers: this.userAnswers, results: true }, '', newURL);

      console.log('[Glowsera Quiz] URL mise a jour pour resultats');

    } catch (error) {
      console.error('[Glowsera Quiz] Erreur mise a jour URL resultats:', error);
    }
  }

  /**
   * Generer une URL partageable avec les resultats
   * @param {Object} userProfile - Profil utilisateur
   * @returns {string} URL partageable
   */
  generateShareableURL(userProfile) {
    try {
      const urlParams = new URLSearchParams();

      // Ajouter les reponses
      const answersJSON = JSON.stringify(this.userAnswers);
      urlParams.set('answers', encodeURIComponent(answersJSON));

      // Ajouter le type de quiz
      urlParams.set('quiz', this.quizType);

      // Marquer comme resultat
      urlParams.set('results', 'true');

      // Generer l'URL complete
      const shareableURL = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;

      console.log('[Glowsera Quiz] URL partageable generee:', shareableURL);

      return shareableURL;

    } catch (error) {
      console.error('[Glowsera Quiz] Erreur generation URL partageable:', error);
      return window.location.href;
    }
  }

  /**
   * Tracker un evenement Google Analytics
   * @param {string} eventName - Nom de l'evenement
   * @param {Object} eventParams - Parametres additionnels
   */
  trackEvent(eventName, eventParams = {}) {
    try {
      // Verifier que gtag existe
      if (typeof gtag === 'undefined') {
        console.warn('[Glowsera Quiz] gtag non disponible, skip tracking');
        return;
      }

      console.log('[Glowsera Quiz] Tracking event:', eventName, eventParams);

      gtag('event', eventName, {
        event_category: 'Quiz',
        event_label: this.quizType,
        ...eventParams
      });

    } catch (error) {
      console.error('[Glowsera Quiz] Erreur tracking:', error);
    }
  }
}

// Export pour utilisation globale
window.GlowseraQuiz = GlowseraQuiz;

/**
 * Fonction globale pour copier l'URL de partage dans le presse-papiers
 * Utilisable depuis les snippets Liquid
 */
window.glowseraCopyShareURL = function() {
  const shareableURL = window.glowseraQuizResults?.shareableURL;

  if (!shareableURL) {
    console.error('[Glowsera Quiz] URL de partage non disponible');
    alert('Erreur: URL de partage non disponible');
    return;
  }

  // Copier dans le presse-papiers
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(shareableURL)
      .then(() => {
        console.log('[Glowsera Quiz] URL copiee:', shareableURL);
        alert('Lien copie dans le presse-papiers!');
      })
      .catch(err => {
        console.error('[Glowsera Quiz] Erreur copie URL:', err);
        // Fallback: afficher l'URL dans un prompt
        prompt('Copiez ce lien:', shareableURL);
      });
  } else {
    // Fallback pour navigateurs anciens
    prompt('Copiez ce lien:', shareableURL);
  }
};

console.log('[Glowsera Quiz] Module core charge avec succes');
